import type { FormEvent } from 'react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { HBox } from '../layout';
import { Button, Icon } from '../basic';
import {
  Breakpoints,
  intl,
  intlFmtTemplateString,
  useBreakpoint,
  useContentEditable,
  useZoomBreakpoint
} from '../../lib';
import { EmojiPickerComp } from './EmojiPickerComp';
import { useLayer } from 'react-laag';
import { DEFAULT_PLACEMENT_CONFIG } from '../../util';
import type { PlacementType } from 'react-laag/dist/PlacementType';
import { FocusTrap } from 'focus-trap-react';
import type { EmojiClickData } from 'emoji-picker-react';
import {
  scaledEmojiNodes,
  scaledEmojiSpan
} from './ChatMessageComponents/scaledEmoji';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';

/**
 * Props for a {@link ChatInput} component.
 */
export interface ChatInputProps {
  /**
   * Handler for when chat message is submitted.
   * @param message message typed in the text input.
   */
  onMessageSubmit: (message: string) => Promise<unknown>;
  showAttachments: boolean;
  /** Called with the files picked via the attachment button. */
  onFilesSelected?: (files: File[]) => void;
  showEmojis: boolean;
  disabled?: boolean;
  /** Keeps the typed text visible but locks the field and its buttons. */
  readOnly?: boolean;
  /**
   * Overrides the default placeholder text shown in the empty input,
   * e.g. to explain why the input is disabled.
   */
  placeholder?: string;
  maxTextLength?: number;
  /** Receives the editable element, e.g. to focus it from the outside. */
  inputRef?: React.RefObject<HTMLElement | null>;
  /**
   * Text to prefill the input.
   */
  initialText?: string;
  /**
   * Called with the current text on every change.
   * Intended for persisting a draft of the not-yet-submitted message.
   */
  onTextChange?: (text: string) => void;
  'data-testid'?: string;
  emojiData?: EmojiData;
}

/**
 * Basic Chat input element.
 *
 * It consists of {@link TextInput} and {@link Button}. When submit is triggered, provided  onMessageSubmit function is fired.

 * @constructor
 */
export function ChatInput({
  onMessageSubmit,
  showAttachments = true,
  onFilesSelected,
  showEmojis = true,
  disabled = false,
  readOnly = false,
  placeholder,
  maxTextLength = 10000,
  inputRef,
  initialText,
  onTextChange,
  emojiData = { categories: {}, emojis: {} },
  ...props
}: Readonly<ChatInputProps>): React.JSX.Element {
  const [classes, setClasses] = useState(['ehs-chat-input']);
  const [errorMessage, setErrorMessage] = useState('');
  const errorId = useId();
  const labelId = useId();
  const testId = props['data-testid'];
  const smallScreen = useBreakpoint(Breakpoints.SMALL);
  const isMobile = useBreakpoint(Breakpoints.MOBILE);
  const isBigScreen = useBreakpoint(Breakpoints.MEDIUMBIG);
  const zoom = useZoomBreakpoint(1.5);

  const handleChange = useCallback(
    (text: string, element: HTMLElement) => {
      setErrorMessage(
        text.length > maxTextLength
          ? intlFmtTemplateString('react.field.string.max', {
              max: maxTextLength.toString()
            })
          : ''
      );
      setClasses(current => {
        const next = current.filter(c => c !== 'error' && c !== 'extended');
        if (text.length > maxTextLength) next.push('error');
        if (
          (element.scrollHeight > 43 ||
            (isMobile && window.innerWidth - element.clientWidth < 100) ||
            (!isMobile &&
              isBigScreen &&
              window.innerWidth - element.clientWidth < 510) ||
            (!isMobile && !isBigScreen && element.innerText.length > 100)) &&
          element.innerText.length > 20
        )
          next.push('extended');
        return next;
      });
      onTextChange?.(text);
    },
    [maxTextLength, isMobile, isBigScreen, onTextChange]
  );

  const editor = useContentEditable({
    toNodes: scaledEmojiNodes,
    onChange: handleChange,
    ref: inputRef
  });
  const message = editor.text;

  // Prefill once on mount, e.g. to restore a draft.
  useEffect(() => {
    if (initialText) {
      editor.insert(initialText, { focus: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onMessageSubmit(message);
    editor.clear();
    editor.focus();
    setClasses(classes.filter(c => c !== 'extended'));
    setOpen(false);
  };

  const addEmoji = (emoji: EmojiClickData) => {
    const name = emoji.names?.[emoji.names.length - 1];
    /*
     * Focus stays out for now: the picker's focus trap is still mounted and
     * would pull it straight back. It is handed to the input once the picker
     * has closed, see the effect below.
     */
    editor.insertNodes([scaledEmojiSpan(emoji.emoji, emojiData, name)], {
      focus: false
    });
    if (!isMobile) {
      focusAfterClose.current = true;
      setOpen(false);
    }
  };

  function getEmojiPickerPlacement(): PlacementType {
    if (isMobile) return 'bottom-start';
    return smallScreen ? 'top-start' : 'top-end';
  }

  const [isOpen, setOpen] = useState(false);
  useEffect(() => {
    if (readOnly) setOpen(false);
  }, [readOnly]);
  const focusAfterClose = useRef(false);

  useEffect(() => {
    if (isOpen || !focusAfterClose.current) return;
    focusAfterClose.current = false;
    editor.focus();
  }, [editor, isOpen]);

  /*
   * On mobile the picker is part of the normal document flow below the input,
   * so react-laag must stay idle: it would otherwise apply absolute positioning
   * and register an outside-click handler for an element it does not own.
   */
  const useFloatingPicker = !isMobile;
  const { layerProps, triggerProps, renderLayer } = useLayer({
    isOpen: isOpen && useFloatingPicker,
    onOutsideClick: () => setOpen(false),
    ...DEFAULT_PLACEMENT_CONFIG,
    placement: getEmojiPickerPlacement()
  });
  const layer = renderLayer(
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: () => {
          setOpen(false);
          editor.focus();
          return true;
        },
        /*
         * Closing the picker must not pull the focus back to the emoji button:
         * the caret belongs behind the freshly inserted emoji in the input.
         */
        returnFocusOnDeactivate: false,
        preventScroll: true,
        initialFocus: false
      }}
    >
      <div {...layerProps} className={zoom ? 'zoom-active' : ''}>
        <EmojiPickerComp
          addMessage={addEmoji}
          emojiData={emojiData}
          data-testid={testId + '-emoji-picker'}
        />
      </div>
    </FocusTrap>
  );

  const locked = disabled || readOnly;
  const sendMessageForbidden =
    locked || message.trim() === '' || errorMessage != '';

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      onFilesSelected?.(Array.from(files));
    }
    // Allow re-picking the same file.
    event.target.value = '';
  };

  /*
   * Plain JSX instead of a nested component: a component declared inside the
   * render function is a new type on every render, which would remount the
   * emoji button and detach the layer trigger ref with it.
   */
  const innerButtons = (
    <>
      {showAttachments && (
        <Button
          variant="tertiary"
          className="attachment"
          text={intl('share.lbAttachment')}
          icon="attachment"
          scale="small"
          onClick={
            onFilesSelected ? () => fileInputRef.current?.click() : undefined
          }
          disabled={locked}
          aria-disabled={locked}
          data-testid={testId && testId + '-attachment-button'}
        />
      )}
      {showEmojis && (
        <Button
          {...triggerProps}
          variant="tertiary"
          className="emoji"
          text={
            isMobile && isOpen
              ? intl('matrixChatPortlet.input.openKeyboard')
              : intl('common.addEmoji')
          }
          icon={isMobile && isOpen ? 'keyboard' : 'emoji'}
          scale="small"
          data-testid={testId && testId + '-emoji-button'}
          onClick={
            isOpen
              ? () => {
                  setOpen(false);
                  editor.focus();
                }
              : () => setOpen(true)
          }
          disabled={locked}
          aria-disabled={locked}
        />
      )}
      {(showEmojis || showAttachments) && (
        <div className="button-divider"></div>
      )}
      <Button
        className="submit"
        icon="send_message"
        text={intl('common.send.message')}
        variant="tertiary"
        type="submit"
        disabled={sendMessageForbidden}
        scale="small"
        data-testid={testId && testId + '-submit-button'}
      />
    </>
  );
  const buttons =
    smallScreen || isMobile ? (
      innerButtons
    ) : (
      <HBox className="button-container">{innerButtons}</HBox>
    );

  const effectivePlaceholder =
    placeholder ?? intl('common.messaging.input.label');

  const sharedInputProps = {
    role: 'textbox',
    'aria-labelledby': labelId,
    'aria-describedby': errorMessage ? errorId : undefined,
    'data-testid': testId + '-message-input'
  } as const;

  return (
    <form
      {...props}
      className={classes.join(' ')}
      onSubmit={handleSubmit}
      data-testid={testId}
    >
      {isOpen && showEmojis && useFloatingPicker && layer}
      {onFilesSelected && (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          disabled={locked}
          className="sr-only"
          onChange={handleFilesChange}
          data-testid={testId && testId + '-attachment-input'}
        />
      )}
      <HBox align="stretch" className="textfield-container">
        <label id={labelId} className="sr-only">
          {intl('common.chatmessage')}
        </label>
        {disabled ? (
          /*
           * Descriptive placeholder in disabled state is rendered as real text
           * because real text is what screen readers read when the mouse hover
           * and it is announced on focus as well.
           * The editable host is not rendered at all while disabled: its hook
           * owns the children of its element, so nothing may be rendered into
           * it from React.
           */
          <div
            {...sharedInputProps}
            aria-disabled="true"
            // A plain div is not focusable, so without this the disabled field
            // could not be reached by keyboard.
            tabIndex={0}
            className="textfield-input disabled-reason"
          >
            {effectivePlaceholder}
          </div>
        ) : (
          <div
            {...editor.editableProps}
            {...sharedInputProps}
            contentEditable={!readOnly}
            aria-readonly={readOnly || undefined}
            // Not focusable once contenteditable is off.
            tabIndex={readOnly ? 0 : undefined}
            aria-multiline="true"
            aria-placeholder={effectivePlaceholder}
            data-placeholder={effectivePlaceholder}
            className="textfield-input"
            onFocus={() => {
              // On mobile the picker replaces the keyboard, so typing closes it.
              if (isMobile) setOpen(false);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                if (!sendMessageForbidden) {
                  event.currentTarget.closest('form')?.requestSubmit();
                }
              }
            }}
          />
        )}
        {buttons}
      </HBox>
      {isOpen && showEmojis && !useFloatingPicker && (
        <div
          className={'emoji-picker-inline' + (zoom ? ' zoom-active' : '')}
          data-testid={testId && testId + '-emoji-picker'}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
        >
          <EmojiPickerComp
            addMessage={addEmoji}
            emojiData={emojiData}
            height="100%"
          />
        </div>
      )}
      {errorMessage && (
        <HBox id={errorId} className="error-message">
          <Icon iconName="shui_error" aria-label={undefined} />
          {errorMessage}
        </HBox>
      )}
    </form>
  );
}
